import React, { useEffect, useRef, useState } from 'react';
import { useAddPhoneNumber } from '../../../hooks/apolloHooks/useAddPhoneNumber';
import { useResendPhoneVerificationCode } from '../../../hooks/apolloHooks/useResendPhoneVerificationCode';
import { useUpdatePhone } from '../../../hooks/apolloHooks/useUpdatePhone';
import { useVerifyPhone } from '../../../hooks/apolloHooks/useVerifyPhone';
import { validatePhone } from '../../../utils/contactValidationSchemas';
import { CloseIcon, InfoIconOutlined } from '../../svgs/ExpressonSVGs';
import Button from '../Button';
import Tooltip from '../Tooltip';

type ProfileDetailsEditPhoneProps = {
	currentPhone: string
	currentPhoneId: number | undefined
	currentPhoneVerified: boolean
	customerId: number
	onUpdateSuccess?: (newPhone?: string) => void
	handleCancel: () => void
}

const ProfileDetailsEditPhone: React.FC<ProfileDetailsEditPhoneProps> = ({
	currentPhone,
	currentPhoneId,
	currentPhoneVerified,
	customerId,
	onUpdateSuccess,
	handleCancel,
}) => {
	const isUnverifiedExisting = !!currentPhoneId && !currentPhoneVerified

	const [phoneValue, setPhoneValue] = useState(currentPhone || '')
	const [phoneError, setPhoneError] = useState<string | undefined>(undefined)
	const [isPhoneValid, setIsPhoneValid] = useState(() => validatePhone(currentPhone || '').valid)

	const [phoneVerificationSent, setPhoneVerificationSent] = useState(false)
	const [isPhoneInputDisabled, setIsPhoneInputDisabled] = useState(isUnverifiedExisting)
	const [sentPhoneValue, setSentPhoneValue] = useState<string>(currentPhone || '')
	const [verifiedPhone, setVerifiedPhone] = useState<string | null>(null)
	const [addedPhoneId, setAddedPhoneId] = useState<number | null>(currentPhoneId ?? null)
	const [codeValue, setCodeValue] = useState('')
	const [submitError, setSubmitError] = useState<string | null>(null)
	const [cooldownSeconds, setCooldownSeconds] = useState(0)

	const phoneInputRef = useRef<HTMLInputElement>(null)
	const codeInputRef = useRef<HTMLInputElement>(null)
	const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null)

	const { addPhoneNumber, loading: addLoading } = useAddPhoneNumber()
	const { updatePhone, loading: updateLoading } = useUpdatePhone()
	const { verifyPhone, loading: verifyLoading } = useVerifyPhone()
	const { resendCode } = useResendPhoneVerificationCode()

	const loading = addLoading || updateLoading

	// Auto-focus code input when verification step appears
	useEffect(() => {
		if (phoneVerificationSent) {
			setTimeout(() => codeInputRef.current?.focus(), 0)
		}
	}, [phoneVerificationSent])

	// Cleanup cooldown interval on unmount
	useEffect(() => {
		return () => { if (cooldownRef.current) clearInterval(cooldownRef.current) }
	}, [])

	const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value
		// Auto-replace leading 0 with +46
		const value = raw === '0' ? '+46' : raw
		setPhoneValue(value)
		const result = validatePhone(value)
		setIsPhoneValid(result.valid)
		setPhoneError(result.valid ? undefined : result.error)
	}

	const handleSendVerification = async () => {
		const result = validatePhone(phoneValue)
		if (!result.valid || !customerId) return
		setSubmitError(null)
		try {
			if (addedPhoneId) {
				// Existing phone — resend or update
				if (phoneValue === sentPhoneValue) {
					if (currentPhoneVerified) {
						// Already verified with same number — nothing to do
						return
					}
					// Unverified same number — resend the verification code
					await resendCode({ phoneNumberId: addedPhoneId })
				} else {
					// Different number — update also triggers a new SMS code
					await updatePhone({
						phoneId: addedPhoneId,
						customerId,
						description: 'Huvudtelefonnummer',
						phoneNo: phoneValue,
						phoneType: 'MAIN',
					})
					setSentPhoneValue(phoneValue)
				}
				setPhoneVerificationSent(true)
				setIsPhoneInputDisabled(true)
			} else {
				// No existing phone — add new
				const added = await addPhoneNumber({
					customerId,
					phoneNo: phoneValue,
				})
				if (added) {
					setPhoneVerificationSent(true)
					setAddedPhoneId(added[0]?.id ?? null)
					setSentPhoneValue(phoneValue)
				}
			}
		} catch (err: any) {
			console.error('[ProfileDetailsEditPhone] send verification failed:', err)
			setSubmitError('Något gick fel. Försök igen.')
		}
	}

	const handleEditPhone = () => {
		setPhoneVerificationSent(false)
		setIsPhoneInputDisabled(false)
		setCodeValue('')
		setSubmitError(null)
		setCooldownSeconds(0)
		if (cooldownRef.current) {
			clearInterval(cooldownRef.current)
			cooldownRef.current = null
		}
		setTimeout(() => phoneInputRef.current?.focus(), 0)
	}

	const handleCodeVerification = async () => {
		if (!codeValue || !addedPhoneId) return
		setSubmitError(null)
		try {
			const verified = await verifyPhone({
				phoneNumberId: addedPhoneId,
				verificationCode: codeValue,
			})
			console.log('[ProfileDetailsEditPhone] Verify result:', verified)
			if (verified?.verified) {
				const verPhone = verified.number ?? phoneValue
				setVerifiedPhone(verPhone)
				setTimeout(() => onUpdateSuccess?.(verPhone), 5000)
			}
		} catch (err: any) {
			console.error('[ProfileDetailsEditPhone] verifyPhone failed:', err)
			setSubmitError('Felaktig verifieringskod. Kontrollera koden och försök igen.')
			setCodeValue('')
		}
	}

	const handleResend = async () => {
		if (!addedPhoneId || cooldownSeconds > 0) return
		try {
			await resendCode({ phoneNumberId: addedPhoneId })
		} catch (err) {
			console.error('[ProfileDetailsEditPhone] resendCode failed:', err)
		}
		setCooldownSeconds(30)
		cooldownRef.current = setInterval(() => {
			setCooldownSeconds(prev => {
				if (prev <= 1) {
					clearInterval(cooldownRef.current!)
					return 0
				}
				return prev - 1
			})
		}, 1000)
	}

	const isVerified = verifiedPhone !== null

	return (
		<>
			<div className="account-phone-edit">
				{/* Success message — shown after verification */}
				{isVerified ? (
					<div className="verification-success">
						<span>Mobilnumret har verifierats!</span>
					</div>
				) : (
					<>
						{/* Phone input */}
						<div className="input-wrapper">
							<label htmlFor="profile-phone">Mobilnummer</label>
							<input
								ref={phoneInputRef}
								type="tel"
								id="profile-phone"
								name="phone"
								placeholder="+467..."
								value={isVerified ? verifiedPhone : phoneValue}
								disabled={phoneVerificationSent || isPhoneInputDisabled}
								onChange={handlePhoneChange}
							/>
							{phoneError && phoneValue.length > 0 && (
								<span className="input-error">{phoneError}</span>
							)}
							{!phoneVerificationSent && !isVerified && (
								<span>Vi skickar en verifieringskod till detta nummer.</span>
							)}
						</div>

						{/* Verification code input — shown after phone submitted */}
						{phoneVerificationSent && !isVerified && (
							<div className="input-wrapper verification">
								<label htmlFor="profile-phoneVerificationCode">
									Verifieringskod
									<Tooltip
										position="top-end"
										text="Verifieringskoden är giltig i 10 dagar. Om du inte har fått någon kod, kontrollera telefonnumret och klicka på 'Skicka verifieringskod igen'."
									>
										<InfoIconOutlined width={25} height={26} />
									</Tooltip>
								</label>
								<input
									ref={codeInputRef}
									type="text"
									inputMode="numeric"
									pattern="[0-9]*"
									id="profile-phoneVerificationCode"
									name="phoneVerificationCode"
									placeholder="Ange verifieringskoden"
									maxLength={4}
									value={codeValue}
									onChange={(e) => setCodeValue(e.target.value.replace(/\D/g, ''))}
								/>
								<span>Fyll i koden du fick i SMS:et för att verifiera ditt mobilnummer.</span>
								<span
									className={`resend-verification${cooldownSeconds > 0 ? ' resend-verification--cooldown' : ''}`}
									onClick={handleResend}
									tabIndex={cooldownSeconds === 0 ? 0 : -1}
									onKeyDown={(e) => {
										if (cooldownSeconds === 0 && (e.key === 'Enter' || e.key === ' ')) {
											e.preventDefault();
											handleResend();
										}
									}}
								>
									{cooldownSeconds > 0
										? `Skicka igen om ${cooldownSeconds}s`
										: 'Skicka verifieringskod igen'
									}
								</span>
							</div>
						)}

						{submitError && (
							<span className="input-error">{submitError}</span>
						)}

						{/* Action buttons */}
						<div className="button-wrapper">
							{!phoneVerificationSent ? (
								<>
									<button
										type="button"
										className="button-edit body-text"
										disabled={!isPhoneValid || loading || (currentPhoneVerified && phoneValue === sentPhoneValue)}
										onClick={handleSendVerification}
									>
										{loading ? 'Skickar...' : 'Skicka verifieringskod'}
									</button>
									{isPhoneInputDisabled && (
										<button
											type="button"
											className="button-edit body-text"
											onClick={handleEditPhone}
										>
											Redigera mobilnummer
										</button>
									)}
								</>
							) : (
								<>
									<button
										type="button"
										className="button-edit body-text"
										disabled={codeValue.length !== 4 || verifyLoading}
										onClick={handleCodeVerification}
									>
										{verifyLoading ? 'Verifierar...' : 'Verifiera mobilnummer'}
									</button>
									<button
										type="button"
										className="button-edit body-text"
										disabled={codeValue.length === 4}
										onClick={handleEditPhone}
									>
										Redigera mobilnummer
									</button>
								</>
							)}
						</div>
					</>
				)}
			</div>

			{/* Close button — hidden while verification is in progress or done */}
			{!phoneVerificationSent && !isVerified && (
				<Button
					type="button"
					className="button-close white-bg"
					onClick={handleCancel}
				>
					<CloseIcon />
				</Button>
			)}
		</>
	)
}

export default ProfileDetailsEditPhone
